import { MarkdownEditor, ViewMode } from '@efdigital/ll-addon-codemirror';
import { All } from '@efdigital/ll-addon-codemirror/markdown-toolbar-options';
import type { CalendarItem, Message } from '@efdigital/ll-addon-data';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Await } from '@warp-drive/ember';

import { EditRecord, type EditRecordSignature } from '../../edit-record.gts';
import { InputDateTime } from '../../input-date.gts';
import CalendarItemsPicker from '../../pickers/calendar-items-picker.gts';

/**
 * Extracts IDs from calendar items, filtering out null/undefined values.
 */
function extractCalendarItemIds(items: CalendarItem[]): string[] {
  return items.map((item) => item.id).filter((id): id is string => Boolean(id));
}

interface EditMessageSignature extends EditRecordSignature<Message> {
  Args: EditRecordSignature<Message>['Args'] & {
    model?: {
      program_id?: string;
      cohort_id?: string;
    };
  };
}

export class EditMessage extends Component<EditMessageSignature> {
  @tracked calendarItemIds: string[] = [];

  constructor(owner: Owner, args: EditMessageSignature['Args']) {
    super(owner, args);
    void this.loadCalendarItemIds();
  }

  async loadCalendarItemIds() {
    const items = this.args.item.calendarItems as unknown as
      | Promise<CalendarItem[] | undefined>
      | CalendarItem[]
      | undefined;

    if (!items) {
      this.calendarItemIds = [];
      return;
    }

    if (Array.isArray(items)) {
      this.calendarItemIds = extractCalendarItemIds(items);
      return;
    }

    const resolved = await items;
    this.calendarItemIds = resolved ? extractCalendarItemIds(resolved) : [];
  }

  get markdown(): string {
    return this.args.item.markdown ?? '';
  }

  setMarkdown = (value: string) => {
    this.args.item.markdown = value ?? '';
  };

  updateTitle = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.title = target.value;
  };

  setStartDate = (value: string) => {
    this.args.item.startDate = value;
  };

  get programId(): string {
    return this.args.model?.program_id ?? '';
  }

  get cohortId(): string {
    return this.args.model?.cohort_id ?? '';
  }

  get category(): string {
    return this.args.item.category || 'announcement';
  }

  get categoryLabel(): string {
    return this.category === 'announcement' ? 'Announcement' : 'Discussion';
  }

  onCalendarItemsSelected = async (calendarItems: CalendarItem[]) => {
    this.calendarItemIds = extractCalendarItemIds(calendarItems);
    // Sync the calendar items to the message record
    const itemsArray = await Promise.resolve(this.args.item.calendarItems);
    if (itemsArray) {
      itemsArray.splice(0, itemsArray.length, ...calendarItems);
    }
  };

  removeCalendarItemAt = async (index: number) => {
    this.calendarItemIds = [
      ...this.calendarItemIds.slice(0, index),
      ...this.calendarItemIds.slice(index + 1),
    ];
    // Sync the removal to the message record
    const itemsArray = await Promise.resolve(this.args.item.calendarItems);
    if (itemsArray) {
      itemsArray.splice(index, 1);
    }
  };

  lookupCalendarItems = async (calendarItemIds: string[]) => {
    const allCalendarItems = await Promise.resolve(
      this.args.item.calendarItems
    );
    if (!allCalendarItems) {
      return [];
    }

    const calendarItems = calendarItemIds.map((calendarItemId) => {
      return allCalendarItems.find(
        (calendarItem) => calendarItem.id === calendarItemId
      );
    });
    return calendarItems.filter((item): item is CalendarItem => Boolean(item));
  };

  <template>
    <EditRecord
      @editLabel="Edit Message"
      @item={{@item}}
      @context={{@context}}
      @initialState={{if @item.id "readonly" "edit"}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">{{if @item.id "Edit" "New"}}
            {{this.categoryLabel}}</legend>

          {{! Category }}
          <div class="mb-3">
            <label
              for="{{uniqueId}}-category"
              class="form-control-label"
            >Category</label>
            <input
              type="text"
              id="{{uniqueId}}-category"
              class="form-control text-capitalize"
              value={{this.category}}
              disabled
              readonly
            />
          </div>

          {{! Title }}
          <div class="mb-3">
            <label for="{{uniqueId}}-title" class="form-control-label required">
              Subject
            </label>
            <input
              type="text"
              id="{{uniqueId}}-title"
              class="form-control"
              value={{@item.title}}
              placeholder="Subject"
              required
              readonly={{isReadOnly}}
              {{on "input" this.updateTitle}}
            />
          </div>

          {{! Markdown Content }}
          <div class="mb-3">
            <label for="{{uniqueId}}-body" class="form-control-label required">
              Message
            </label>
            {{#if @item}}
              {{#if isReadOnly}}
                <textarea
                  id="{{uniqueId}}-body"
                  class="form-control"
                  rows="10"
                  required
                  readonly
                >{{this.markdown}}</textarea>
              {{else}}
                <MarkdownEditor
                  @markdown={{this.markdown}}
                  @onUpdate={{this.setMarkdown}}
                  @mode={{ViewMode.SplitHorizontal}}
                  @toolbar={{All}}
                />
              {{/if}}
            {{/if}}
            <div class="form-text">Messages support Markdown formatting.</div>
          </div>

          {{! Publish Date }}
          <div class="mb-3">
            <label for="{{uniqueId}}-publishDate" class="form-control-label">
              Publish Date
            </label>
            <InputDateTime
              id="{{uniqueId}}-publishDate"
              name="publishDate"
              class="form-control"
              readonly={{isReadOnly}}
              @value={{@item.startDate}}
              @onChange={{this.setStartDate}}
            />
            <div class="form-text">Leave blank to publish immediately.</div>
          </div>

          {{! Calendar Items }}
          {{#if this.programId}}
            {{#if this.cohortId}}
              <div class="mb-3">
                <label class="form-control-label">Calendar Items</label>
                <CalendarItemsPicker
                  @programId={{this.programId}}
                  @cohortId={{this.cohortId}}
                  @calendarItemIds={{this.calendarItemIds}}
                  @onSelect={{this.onCalendarItemsSelected}}
                >
                  <:input as |showModal|>
                    <div class="mb-2">
                      {{#if this.calendarItemIds.length}}
                        <Await
                          @promise={{this.lookupCalendarItems
                            this.calendarItemIds
                          }}
                        >
                          <:success as |calendarItems|>
                            <div class="d-flex flex-column gap-2">
                              {{#each calendarItems as |calendarItem index|}}
                                <div class="input-group">
                                  <input
                                    type="text"
                                    class="form-control"
                                    value={{calendarItem.title}}
                                    aria-label="Calendar item"
                                    readonly
                                  />
                                  {{#unless isReadOnly}}
                                    <button
                                      aria-label="Remove calendar item"
                                      class="btn btn-outline-danger"
                                      type="button"
                                      {{on
                                        "click"
                                        (fn this.removeCalendarItemAt index)
                                      }}
                                    >
                                      <iconify-icon inline icon="ph:x-bold" />
                                    </button>
                                  {{/unless}}
                                </div>
                              {{/each}}
                            </div>
                          </:success>
                        </Await>
                      {{/if}}
                    </div>
                    {{#unless isReadOnly}}
                      <button
                        type="button"
                        class="btn btn-primary btn-sm"
                        {{on "click" (fn showModal this.calendarItemIds)}}
                      >
                        <iconify-icon inline icon="ph:calendar-plus" />
                        {{if
                          this.calendarItemIds.length
                          "Manage Calendar Items"
                          "Add Calendar Items"
                        }}
                      </button>
                    {{/unless}}
                  </:input>
                </CalendarItemsPicker>
              </div>
            {{/if}}
          {{/if}}
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}
